// Concept Art Gallery - Standalone React Component
// Does NOT load the game, just displays concept art images

import React, { useState } from 'react';

interface ConceptArt {
    key: string;
    path: string;
    title: string;
    description: string;
}

const concepts: ConceptArt[] = [
  {
    key: 'chao_base',
    path: '/assets/concepts/chao_base.png',
    title: 'Spooky Chao - Base Spirit',
    description: 'The evolving spirit form. Cute but mysterious.'
  },
  {
    key: 'chao_warrior',
    path: '/assets/concepts/chao_warrior.png',
    title: 'Spooky Chao - Warrior',
    description: 'Devil-Chao evolution with horns and heavy gear.'
  },
  {
    key: 'chao_mage',
    path: '/assets/concepts/chao_mage.png',
    title: 'Spooky Chao - Mage',
    description: 'Ghost-Chao evolution with magical aura.'
  },
  {
    key: 'cloak_group',
    path: '/assets/concepts/cloak_group.png',
    title: 'Cloaked Heroes - Squad',
    description: 'Original "Power Ranger" concept art.'
  },
  {
    key: 'cloak_ranger',
    path: '/assets/concepts/cloak_ranger.png',
    title: 'Cloaked Hero - Ranger',
    description: 'Sneaky leaf design.'
  },
  {
    key: 'cloak_paladin',
    path: '/assets/concepts/cloak_paladin.png',
    title: 'Cloaked Hero - Paladin',
    description: 'Holy marshmallow design.'
  }
];

interface ConceptArtGalleryProps {
    onBack: () => void;
}

export const ConceptArtGallery: React.FC<ConceptArtGalleryProps> = ({ onBack }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageError, setImageError] = useState(false);

  const navigate = (direction: number) => {
    setImageError(false);
    let newIndex = currentIndex + direction;
    if (newIndex < 0) newIndex = concepts.length - 1;
    if (newIndex >= concepts.length) newIndex = 0;
    setCurrentIndex(newIndex);
  };

  const concept = concepts[currentIndex];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px',
      fontFamily: '\'Inter\', \'Segoe UI\', sans-serif',
      color: '#ffffff'
    }}>
      {/* Header */}
      <div style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <button
          onClick={onBack}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            color: '#ffffff',
            cursor: 'pointer',
            transition: 'all 0.2s',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
          }}
        >
                    ← Back to Menu
        </button>
        <h1 style={{
          fontSize: '32px',
          fontWeight: 'bold',
          textShadow: '0 2px 20px rgba(255, 107, 0, 0.5)',
          margin: 0
        }}>
                    🎨 Concept Art Gallery
        </h1>
        <div style={{ width: '140px' }} /> {/* Spacer for centering */}
      </div>

      {/* Image Counter */}
      <div style={{
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.6)',
        marginBottom: '10px'
      }}>
        {currentIndex + 1} / {concepts.length}
      </div>

      {/* Main Gallery Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '1200px',
        gap: '20px'
      }}>
        {/* Previous Button */}
        <button
          onClick={() => navigate(-1)}
          style={{
            width: '60px',
            height: '60px',
            fontSize: '32px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            color: '#ffffff',
            cursor: 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 107, 0, 0.3)';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
                    ‹
        </button>

        {/* Image Display */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '16px',
          padding: '20px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          minHeight: '500px'
        }}>
          {imageError ? (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255, 255, 255, 0.5)'
            }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>🖼️</div>
              <div>Image not available</div>
              <div style={{ fontSize: '12px', marginTop: '5px' }}>
                {concept.path}
              </div>
            </div>
          ) : (
            <img
              src={concept.path}
              alt={concept.title}
              onError={() => setImageError(true)}
              style={{
                maxWidth: '100%',
                maxHeight: '400px',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
              }}
            />
          )}
        </div>

        {/* Next Button */}
        <button
          onClick={() => navigate(1)}
          style={{
            width: '60px',
            height: '60px',
            fontSize: '32px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            color: '#ffffff',
            cursor: 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 107, 0, 0.3)';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
                    ›
        </button>
      </div>

      {/* Title and Description */}
      <div style={{
        textAlign: 'center',
        marginTop: '20px',
        padding: '20px',
        background: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '800px'
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          marginBottom: '10px',
          color: '#ff6b00'
        }}>
          {concept.title}
        </h2>
        <p style={{
          fontSize: '16px',
          color: 'rgba(255, 255, 255, 0.7)',
          margin: 0
        }}>
          {concept.description}
        </p>
      </div>

      {/* Thumbnail Navigation */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginTop: '20px',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        {concepts.map((c, index) => (
          <button
            key={c.key}
            onClick={() => {
              setImageError(false);
              setCurrentIndex(index);
            }}
            style={{
              width: '80px',
              height: '60px',
              padding: '4px',
              background: currentIndex === index 
                ? 'rgba(255, 107, 0, 0.4)' 
                : 'rgba(255, 255, 255, 0.1)',
              border: currentIndex === index 
                ? '2px solid #ff6b00' 
                : '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              overflow: 'hidden'
            }}
            title={c.title}
          >
            <img
              src={c.path}
              alt={c.title}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '4px'
              }}
            />
          </button>
        ))}
      </div>

      {/* Keyboard Navigation Hint */}
      <div style={{
        marginTop: '20px',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.4)'
      }}>
                Use arrow keys or click buttons to navigate
      </div>
    </div>
  );
};
